const TAU_HOURS = {
  "once-daily": 24,
  "twice-daily": 12,
  weekly: 168,
};

const STEADY_STATE_HALF_LIVES = 5;
const CURVE_MIN_HOURS = 14 * 24;
const CURVE_MIN_INTERVALS = 6;
const CURVE_STEP_HOURS = 1;

export function calculateHalfLife({ halfLifeHours, frequency }) {
  const tauHours = TAU_HOURS[frequency];

  if (!Number.isFinite(halfLifeHours) || halfLifeHours <= 0 || !tauHours) return { ok: false };

  const k = Math.log(2) / halfLifeHours;
  const decayPerTau = Math.exp(-k * tauHours);
  const relativePeakAtSteadyState = 1 / (1 - decayPerTau);
  const relativeTroughAtSteadyState = relativePeakAtSteadyState * decayPerTau;

  return {
    ok: true,
    halfLifeHours,
    tauHours,
    steadyStateHours: STEADY_STATE_HALF_LIVES * halfLifeHours,
    relativePeakAtSteadyState,
    relativeTroughAtSteadyState,
    peakToTroughRatio: relativePeakAtSteadyState / relativeTroughAtSteadyState,
    curve: curve(k, tauHours),
  };
}

function curve(k, tauHours) {
  const targetHours = Math.max(CURVE_MIN_HOURS, tauHours * CURVE_MIN_INTERVALS);
  const samples = [];
  let residual = 0;
  let nextDoseHour = 0;

  for (let hour = 0; hour <= targetHours; hour += CURVE_STEP_HOURS) {
    const isDose = hour >= nextDoseHour;

    if (isDose) {
      residual += 1;
      nextDoseHour += tauHours;
    }

    samples.push({ hour, level: residual, isDose });
    residual *= Math.exp(-k * CURVE_STEP_HOURS);
  }

  return samples;
}

export function interpretation(result) {
  const accumulation = accumulationLabel(result.relativePeakAtSteadyState);
  const stability = stabilityLabel(result.peakToTroughRatio);

  return `At this schedule the compound ${accumulation} (peak ~${result.relativePeakAtSteadyState.toFixed(2)}x a single dose) and ${stability}. ${steadyStateSentence(result.steadyStateHours)}`;
}

export function formatDuration(hours) {
  if (hours < 24) return `${Math.round(hours)} h`;

  return `${Number.parseFloat((hours / 24).toFixed(1))} d`;
}

function accumulationLabel(peak) {
  if (peak < 1.2) return "barely accumulates";
  if (peak < 2) return "accumulates modestly";
  if (peak < 4) return "accumulates significantly";

  return "accumulates heavily";
}

function stabilityLabel(ratio) {
  if (ratio < 1.5) return "levels stay stable between doses";
  if (ratio < 3) return "levels swing moderately between doses";

  return "levels swing widely between doses";
}

function steadyStateSentence(hours) {
  if (hours < 24) {
    return `Steady state reached in about ${Math.round(hours)} hours.`;
  }

  return `Steady state reached in about ${(hours / 24).toFixed(1)} days.`;
}
